<template>
    <div v-if="show" class="qi-fab-wrap" :class="{ 'qi-fab-wrap--pos': isPosPage }">
        <div v-if="open" class="qi-fab-backdrop" @click="open = false"></div>
        <transition name="qi-fab-menu">
            <div v-if="open" class="qi-fab-menu">
                <a v-for="l in visibleLinks" :key="l.key" :href="l.href" class="qi-fab-menu-item">
                    <i class="bi text-primary" :class="l.icon"></i> {{ l.label }}
                </a>
                <a href="#" class="qi-fab-menu-item border-top text-muted" @click.prevent="openCustomize">
                    <i class="bi bi-three-dots"></i> Customize
                </a>
            </div>
        </transition>

        <!-- One action (no menu to put it in): customize from the button's corner -->
        <button v-if="single" type="button" class="qi-fab-more btn btn-light" @click="openCustomize" aria-label="Customize quick actions" title="Customize quick actions">
            <i class="bi bi-three-dots"></i>
        </button>

        <!-- One action left: go straight there. Several: open the menu. -->
        <button type="button" class="qi-fab-btn btn btn-primary" @click="onMainClick" :aria-label="single ? single.label : 'Create'">
            <i class="bi" :class="open ? 'bi-x-lg' : (single ? single.icon : 'bi-pencil-square')"></i>
            <span class="qi-fab-tooltip d-none d-md-inline">{{ single ? single.label : 'Create' }}</span>
        </button>

        <b-modal ref="customize" title="Customize quick actions" hide-footer centered>
            <p class="text-muted mb-2">Choose what the floating button shows. With one choice it opens that page directly.</p>
            <div v-for="l in availableLinks" :key="l.key" class="custom-control custom-checkbox mb-2">
                <input type="checkbox" class="custom-control-input" :id="'fab-' + l.key" :value="l.key" v-model="draft">
                <label class="custom-control-label" :for="'fab-' + l.key"><i class="bi text-primary mr-1" :class="l.icon"></i>{{ l.label }}</label>
            </div>
            <small v-if="!draft.length" class="text-danger d-block mb-2">Keep at least one.</small>
            <div class="d-flex justify-content-between mt-3">
                <button type="button" class="btn btn-outline-secondary" @click="draft = availableLinks.map(l => l.key)">Show all</button>
                <button type="button" class="btn btn-primary" :disabled="!draft.length" @click="saveCustomize">Save</button>
            </div>
        </b-modal>
    </div>
</template>

<script>
export default {
    name: 'CreateFab',
    data() {
        return {
            open: false,
            isPosPage: window.location.pathname === '/makesale',
            chosen: null, // keys the user kept; null = all
            draft: [],
        };
    },
    created() {
        try {
            const saved = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
            if (Array.isArray(saved) && saved.length) this.chosen = saved;
        } catch (e) { /* storage unavailable */ }
    },
    computed: {
        show() {
            // Auth-gated: this component is only mounted inside the @auth block in
            // layouts/app.blade.php, so no separate login check is needed here.
            return true;
        },
        settings() {
            const u = this.$store.getters.userDetails;
            return (u && u.company && u.company.companysettings) || {};
        },
        storageKey() {
            const u = this.$store.getters.userDetails;
            return 'createFabLinks:' + ((u && u.id) || 'me');
        },
        // Every action this business can use.
        availableLinks() {
            const s = this.settings;
            return [
                { key: 'quote', href: '/quotes/create', icon: 'bi-file-earmark-text', label: 'Create Quote', on: !!s.has_invoices },
                { key: 'invoice', href: '/invoices/create', icon: 'bi-receipt', label: 'Create Invoice', on: !!s.has_invoices },
                { key: 'subscription', href: '/subscriptions?openCreate=1', icon: 'bi-arrow-repeat', label: 'Add Subscription', on: !!s.has_subscriptions },
                { key: 'reservation', href: '/make-reservations', icon: 'bi-calendar-check', label: 'Add Reservation', on: !!s.has_reservations },
                { key: 'sale', href: '/makesale', icon: 'bi-cart-plus', label: 'Create Order/Sale', on: true },
                { key: 'customer', href: '/customers?openCreate=1', icon: 'bi-person-plus', label: 'Add Customer', on: true },
                { key: 'expense', href: '/expenses?openCreate=1', icon: 'bi-cash-stack', label: 'Add Expense', on: true },
                { key: 'inventory', href: '/inventory?openCreate=1', icon: 'bi-box-seam', label: 'Add Inventory Item', on: true },
            ].filter(l => l.on);
        },
        visibleLinks() {
            const kept = this.chosen ? this.availableLinks.filter(l => this.chosen.includes(l.key)) : this.availableLinks;
            // A saved choice that no longer applies (feature turned off) falls back to all.
            return kept.length ? kept : this.availableLinks;
        },
        single() {
            return this.visibleLinks.length === 1 ? this.visibleLinks[0] : null;
        },
    },
    methods: {
        onMainClick() {
            if (this.single && !this.open) {
                window.location.href = this.single.href;
                return;
            }
            this.open = !this.open;
        },
        openCustomize() {
            this.open = false;
            this.draft = this.visibleLinks.map(l => l.key);
            this.$refs.customize.show();
        },
        saveCustomize() {
            if (!this.draft.length) return;
            const all = this.draft.length === this.availableLinks.length;
            this.chosen = all ? null : [...this.draft];
            try {
                if (all) localStorage.removeItem(this.storageKey);
                else localStorage.setItem(this.storageKey, JSON.stringify(this.chosen));
            } catch (e) { /* storage unavailable */ }
            this.$refs.customize.hide();
        },
    },
};
</script>

<style scoped>
.qi-fab-wrap {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 1040;
}
.qi-fab-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1039;
}
.qi-fab-btn {
    position: relative;
    width: 56px;
    height: 56px;
    padding: 0;
    border-radius: 50%;
    font-size: 1.35rem;
    border: 3px solid #fff;
    box-shadow: 0 4px 16px rgba(0,0,0,.35);
    display: flex;
    align-items: center;
    justify-content: center;
}
.qi-fab-more {
    position: absolute;
    top: -10px;
    left: -10px;
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 50%;
    font-size: .9rem;
    line-height: 1;
    z-index: 1041;
    box-shadow: 0 2px 6px rgba(0,0,0,.25);
}
.qi-fab-btn:hover .qi-fab-tooltip { opacity: 1; transform: translateX(0); }
.qi-fab-tooltip {
    position: absolute;
    right: 100%;
    margin-right: 12px;
    background: #212529;
    color: #fff;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: .8rem;
    white-space: nowrap;
    opacity: 0;
    transform: translateX(6px);
    transition: opacity .15s ease, transform .15s ease;
    pointer-events: none;
}
.qi-fab-menu {
    position: absolute;
    bottom: 66px;
    right: 0;
    z-index: 1040;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(0,0,0,.2);
    min-width: 210px;
    padding: 6px;
    display: flex;
    flex-direction: column;
}
.qi-fab-menu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 8px;
    color: #212529;
    font-size: .9rem;
}
.qi-fab-menu-item:hover { background: #f8f9fa; text-decoration: none; color: #212529; }
.qi-fab-menu-item i { width: 18px; text-align: center; }

.qi-fab-menu-enter-active, .qi-fab-menu-leave-active { transition: opacity .12s ease, transform .12s ease; }
.qi-fab-menu-enter, .qi-fab-menu-leave-to { opacity: 0; transform: translateY(6px); }

/* Mobile: sit above the bottom nav bar and respect safe-area insets. */
@media only screen and (max-width: 768px) {
    .qi-fab-wrap {
        right: 16px;
        bottom: calc(76px + env(safe-area-inset-bottom, 0px));
    }
    /* On the POS/Sales page the cart's own .float button already occupies the
       bottom-right corner (60px, bottom:70px, right:40px) - sit this FAB in
       line with it (same row, just to its left) instead of stacking above. */
    .qi-fab-wrap--pos {
        bottom: calc(70px + env(safe-area-inset-bottom, 0px));
        right: 114px;
    }
}
</style>
